/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from "react";
import { Form } from "react-bootstrap";
import { Button, Drawer, Modal, Tooltip, Checkbox } from "antd";
import { FaBell } from "react-icons/fa";
import { v4 as uuidv4 } from "uuid";
import {
  fetchClientReminderThunk,
  postClientClientReminderApi,
  clearUpdateMessage,
} from "../../redux/Slices/clientReminderSlice";
import DatePicker from "react-datepicker";
import FormControl from "../../components/Common/FormControl";
import ToastNotification from "../../components/Common/ToastNotification";
import { useDispatch, useSelector } from "react-redux";
import FormLabel from "../../components/Common/FormLabel";
import useCustomTranslation from "../../hooks/useCustomTranslation";
import { CONSTANTS, parseLocalDate } from "../../utils/Constant/Constant";
import { formatDate } from "../../utils/Utils";
import appInsights from "../../AppInsights";

function Reminder({ selectedClientId }) {
  const { trans } = useCustomTranslation();
  const dispatch = useDispatch();
  const currentDate = new Date();
  currentDate.setHours(0, 0, 0, 0); // Normalize to start of today

  const { ClientReminderLoad, updateSuccessMessage, updateFailedMessage } =
    useSelector((state) => state.clientReminder);

  const [visible, setVisible] = useState(false);
  const [editToggle, setEditToggle] = useState(false);
  const [addReminder, setAddReminder] = useState(false);
  const [selectedButtonDate, setSelectedButtonDate] = useState(null);
  const [toastConfig, setToastConfig] = useState(null);
  const [errors, setErrors] = useState({});
  const [selectedForm, setSelectedForm] = useState({
    clientReminderId: "",
    clientId: selectedClientId,
    reminderName: "",
    reminderDate: "",
    isCompleted: false,
  });

  const [form, setForm] = useState([]);
  const { profileData } = useSelector((state) => state.profile);
  const coachId = profileData?.coachId;
  
  useEffect(() => {
    if (selectedClientId) {
      dispatch(
        fetchClientReminderThunk({
          clientId: selectedClientId,
        })
      );
    // Track Page View
    appInsights.trackPageView({
      name: "Client_Reminder_Page",
      properties: { clientId: selectedClientId, coachId:coachId },
    });
    }
  }, [dispatch, selectedClientId]);

  useEffect(() => {
    if (Array.isArray(ClientReminderLoad)) {
      const mappedData = ClientReminderLoad.map((reminder) => ({
        clientReminderId: reminder.reminderId,
        clientId: reminder.clientId,
        reminderName: reminder.reminderName,
        reminderDate: reminder.reminderDate,
        isCompleted: reminder.isCompleted,
        CreatedBy: reminder.CreatedBy,
      }));
      setForm(mappedData);
    }
  }, [ClientReminderLoad, selectedClientId]);

  useEffect(() => {
    if (updateSuccessMessage) {
      setToastConfig({
        type: CONSTANTS.TOAST_SUCCESS,
        message: updateSuccessMessage,
        heading: trans("Common.success"),
      });
      setTimeout(() => {
        dispatch(clearUpdateMessage());
        setToastConfig(null);
      }, 3000);
    } else if (updateFailedMessage) {
      setToastConfig({
        type: CONSTANTS.TOAST_ERROR,
        message: updateFailedMessage,
        heading: trans("Common.failed"),
      });
      setTimeout(() => {
        dispatch(clearUpdateMessage());
        setToastConfig(null);
      }, 3000);
    }
  }, [updateSuccessMessage, updateFailedMessage, trans, dispatch]);

  const toggleCompletion = (clientReminderId, flag) => {
    const item = form.find(
      (reminder) => reminder.clientReminderId === clientReminderId
    );
    if (item) {
      const updatedItem = {
        ...item,
        isCompleted: !flag,
        CreatedBy: selectedClientId,
      };

      // Track Toggle Completion
      appInsights.trackEvent({
        name: "Client_Reminder_Toggled",
        properties: {
          clientId: selectedClientId,
          reminderId: clientReminderId,
          completed: !flag,
          coachId: coachId
        },
      });

      const updatedForm = form.map((item) =>
        item.clientReminderId === clientReminderId ? updatedItem : item
      );
      setForm(updatedForm);
      dispatch(postClientClientReminderApi({ payload: updatedItem }));
    }
  };

  const countFutureReminders = (reminders) => {
    return reminders?.filter((reminder) => {
      const reminderDate = parseLocalDate(reminder.reminderDate);
      return reminderDate >= currentDate && !reminder.isCompleted;
    }).length;
  };

  const buttonGrpChange = (reminderDateOffset) => {
    setSelectedButtonDate(reminderDateOffset);
    const current = new Date();
    current.setHours(0, 0, 0, 0);
    current.setDate(current.getDate() + reminderDateOffset);

    const formattedDate = `${current.getFullYear()}-${String(
      current.getMonth() + 1
    ).padStart(2, "0")}-${String(current.getDate()).padStart(2, "0")}`;

    setSelectedForm((prev) => ({
      ...prev,
      reminderDate: formattedDate,
    }));
  };

  const editReminder = (item) => {
    // Track Edit Click
    appInsights.trackEvent({
      name: "Client_Reminder_Edit_Clicked",
      properties: {
        clientId: selectedClientId,
        reminderId: item.clientReminderId,
        coachId: coachId
      },
    });

    setErrors({});
    setSelectedButtonDate(null);
    setAddReminder(true);
    setSelectedForm({
      clientReminderId: item.clientReminderId,
      clientId: item.clientId,
      reminderName: item.reminderName,
      reminderDate: item.reminderDate,
      isCompleted: item.isCompleted,
      CreatedBy: selectedClientId,
    });
    setEditToggle(true);
  };

  const handleDateChange = (date) => {
    setErrors((prev) => ({
      ...prev,
      reminderDate: null,
    }));
    setSelectedButtonDate(null);
    if (date) {
      const formattedDate = `${date.getFullYear()}-${String(
        date.getMonth() + 1
      ).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;

      setSelectedForm((prev) => ({ ...prev, reminderDate: formattedDate }));
    } else {
      setSelectedForm((prev) => ({ ...prev, reminderDate: "" }));
    }
  };

  const handleEventChange = (event) => {
    setErrors((prev) => ({
      ...prev,
      reminderName: null,
    }));
    setSelectedForm((prev) => ({
      ...prev,
      reminderName: event.target.value,
    }));
  };

  const onOkay = () => {
    setErrors({});
    const newErrors = {};

    if (!selectedForm.reminderName.trim()) {
      newErrors.reminderName = trans("ClientWorkout.eventNameRequired");
    }
    if (!selectedForm.reminderDate) {
      newErrors.reminderDate = trans("ClientWorkout.dateRequired");
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    if (selectedForm.reminderName && selectedForm.reminderDate) {
    // Track Create / Update
    appInsights.trackEvent({
      name: editToggle
        ? "Client_Reminder_Updated"
        : "Client_Reminder_Created",
      properties: {
        clientId: selectedClientId,
        coachId: coachId,
        reminderId: selectedForm.clientReminderId,
        reminderDate: selectedForm.reminderDate,
      },
    });

      if (!editToggle) {
        setForm((prevForm) => [...prevForm, selectedForm]);
      } else {
        setForm((prevForm) =>
          prevForm.map((reminder) =>
            reminder.clientReminderId === selectedForm.clientReminderId
              ? selectedForm
              : reminder
          )
        );
      }

      dispatch(
        postClientClientReminderApi({
          payload: selectedForm,
        })
      ).then(() => {
        dispatch(
          fetchClientReminderThunk({
            clientId: selectedClientId,
          })
        );
      });

      setSelectedForm({
        clientReminderId: "",
        clientId: "",
        reminderName: "",
        reminderDate: "",
        isCompleted: false,
      });
    }

    setAddReminder(false);
    setEditToggle(false);
  };

  const onCancel = () => {
    if (editToggle) {
      setEditToggle(false);
    }
    setAddReminder(false);
    setSelectedForm({
      clientReminderId: "",
      clientId: "",
      reminderName: "",
      reminderDate: "",
      isCompleted: false,
    });
    setErrors({});
  };

  const showDrawer = () => {
    // Track Drawer Open
    appInsights.trackEvent({
      name: "Client_Reminder_Drawer_Opened",
      properties: { 
        clientId: selectedClientId,
        coachId:coachId
       },
    });

    setVisible(true);
  };

  const closeDrawer = () => {
    setVisible(false);
  };

  const addButtonClick = () => {
    // Track Add Click
    appInsights.trackEvent({
      name: "Client_Reminder_Add_Clicked",
      properties: { 
        clientId: selectedClientId,
        coachId:coachId 
      },
    });

    setEditToggle(false);
    setAddReminder(!addReminder);
    setSelectedButtonDate(null);
    setErrors({});
    setSelectedForm({
      clientReminderId: uuidv4(),
      clientId: selectedClientId,
      reminderName: "",
      reminderDate: "",
      isCompleted: false,
      CreatedBy: selectedClientId,
    });
  };

  return (
    <>
      <div className="d-flex">
        <div onClick={showDrawer} className="d-flex bell-icon-reminder">
          <Tooltip title={trans("ClientWorkout.reminder")} placement="top">
            <FaBell size={25} />
          </Tooltip>
          {form.length > 0 && countFutureReminders(form) > 0 && (
            <span className="notification-count-reminder">
              {countFutureReminders(form)}
            </span>
          )}
        </div>
      </div>

      <Drawer
        className="reminder-client"
        title={
          <div className="d-flex">
            <div className="reminder-heading">{trans("ClientWorkout.reminder")}</div>
            <div className="ms-auto">
              <button onClick={addButtonClick} className="mt-3 add-plan-button">
                <i className="bi bi-plus"> </i>
                {trans("ClientWorkout.add")}
              </button>
            </div>
          </div>
        }
        placement="right"
        width="30%"
        onClose={closeDrawer}
        open={visible}
      >
        <div>
          <ul className="reminder-list">
            {form.length > 0 ? (
              form.map((item) => {
                return (
                  <li className="Reminder-list" key={item.clientReminderId}>
                    <div
                      className={
                        item.IsEditable
                          ? "reminder-checkbox-style-past"
                          : "reminder-checkbox-style-current"
                      }
                    >
                      <div>
                        <Checkbox
                          className="checkbox-reminder"
                          checked={item.isCompleted}
                          onChange={() => {
                            toggleCompletion(item.clientReminderId, item.isCompleted);
                          }}
                        />
                      </div>
                      <div className="reminder-body">
                        <div
                          className={
                            item.isCompleted
                              ? "event-reminder-completed"
                              : "event-reminder-not-completed"
                          }
                        >
                          {item.reminderName}
                        </div>
                        <div
                          className={
                            item.isCompleted
                              ? "date-reminder-completed"
                              : "date-reminder-not-completed"
                          }
                        >
                          {formatDate(item.reminderDate)}
                        </div>
                      </div>
                      <div className="pencil-reminder ms-auto">
                        <i
                          onClick={() => {
                            if (!item.IsEditable) {
                              editReminder(item);
                            }
                          }}
                          className="pencil-size bi bi-pencil"
                          style={{
                            cursor: item.IsEditable ? "not-allowed" : "pointer",
                          }}
                        ></i>
                      </div>
                    </div>
                  </li>
                );
              })
            ) : (
              <div className="middle-text">{trans("ClientWorkout.noReminderFound")}</div>
            )}
          </ul>
        </div>
      </Drawer>

      <Modal
        title={
          <div>
            {editToggle ? (
              <div>{trans("ClientWorkout.editReminder")}</div>
            ) : (
              <div>{trans("ClientWorkout.createReminder")}</div>
            )}
          </div>
        }
        visible={addReminder}
        onCancel={onCancel}
        footer={null} // Using custom footer buttons
      >
        <div>
          <Button.Group>
            <Button
              className="seven-days"
              onClick={() => {
                buttonGrpChange(7);
              }}
              style={{
                backgroundColor:
                  selectedButtonDate === 7 ? "#383D8D" : "transparent",
                color: selectedButtonDate === 7 ? "white" : "black",
              }}
            >
              {trans("ClientWorkout.insevenDays")}
            </Button>
            <Button
              className="seven-days"
              onClick={() => {
                buttonGrpChange(14);
              }}
              style={{
                backgroundColor:
                  selectedButtonDate === 14 ? "#383D8D" : "transparent",
                color: selectedButtonDate === 14 ? "white" : "black",
              }}
            >
              {trans("ClientWorkout.inFourteenDays")}
            </Button>
            <Button
              className="seven-days"
              onClick={() => {
                buttonGrpChange(30);
              }}
              style={{
                backgroundColor:
                  selectedButtonDate === 30 ? "#383D8D" : "transparent",
                color: selectedButtonDate === 30 ? "white" : "black",
              }}
            >
              {trans("ClientWorkout.inThreetyDays")}
            </Button>
          </Button.Group>

          <Form.Group controlId="formTargetDate">
            <FormLabel label={trans("ClientWorkout.date")} required />
            <div className="d-flex flex-column">
              <div className=" position-relative date-picker fit-width-reminder">
                <span className="date-picker">
                  <DatePicker
                    className={`custom-datepicker w-100 ${
                      errors.reminderDate
                        ? "datepicker-width-reminder-invalid"
                        : "datepicker-width-reminder"
                    }`}
                    selected={
                      selectedForm.reminderDate
                        ? parseLocalDate(selectedForm.reminderDate)
                        : null
                    }
                    onChange={handleDateChange}
                    dateFormat="MMM dd, yyyy"
                    placeholderText={trans("ClientWorkout.selectDate")}
                    minDate={new Date()}
                    dayClassName={(date) => {
                      const selectedDate = selectedForm.reminderDate
                        ? parseLocalDate(selectedForm.reminderDate)
                        : null;
                      return selectedDate &&
                        date.toDateString() === selectedDate.toDateString()
                        ? "inline-selected-day"
                        : undefined;
                    }}
                  />

                  <style>
                    {`
      .inline-selected-day {
        background-color: #383D8D !important;
        color: white !important;
      }
      .react-datepicker__day--keyboard-selected {
        background-color: #383D8D !important;
        color: white !important;
        border-radius: 0.3rem !important;
      }
    `}
                  </style>

                  <i className="bi bi-calendar3 calendar-icon"></i>
                </span>
              </div>
              {errors.reminderDate && (
                <div className="invalid-feedback d-block">{errors.reminderDate}</div>
              )}
            </div>
          </Form.Group>

          <div>
            <FormLabel
              label={trans("ClientWorkout.reminder")}
              required={true}
              className="mt-2"
            />
            <FormControl
              type="text"
              name="event"
              value={selectedForm.reminderName}
              onChange={handleEventChange}
              className="reminder-event rounded-3"
              isInvalid={!!errors.reminderName}
              feedback={errors.reminderName}
            />
          </div>
        </div>

        <div className="modal-footer">
          <Button
            variant="outline-primary"
            onClick={onCancel}
            size="sm"
            className="mt-2 mx-2 back_button w-25 p-2"
          >
            {trans("Common.cancel")}
          </Button>

          <Button
            className="mt-2 saveButton-reminder w-25 ml-2"
            onClick={onOkay}
          >
            {editToggle
              ? trans("ClientWorkout.update")
              : trans("ClientWorkout.create")}
          </Button>
        </div>
      </Modal>

      {toastConfig && (
        <ToastNotification
          type={toastConfig.type}
          message={toastConfig.message}
          heading={toastConfig.heading}
        />
      )}
    </>
  );
}

export default Reminder;
